import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "core/store/authStore";
import { useProviderStore } from "core/store/providerStore";
import { invalidateFleetQueries } from "core/query/invalidateFleet";
import {
  createOrgInvitation,
  listOrgInvitations,
  resendOrgInvitation,
  revokeOrgInvitation,
} from "features/invitations/services/invitationApi";
import {
  disableOrgDriver,
  enableOrgDriver,
  listOrgDrivers,
  listOrgMembers,
  listOrgRoles,
  removeOrgMember,
  setDriverVehicleAccess,
  updateOrgMemberRole,
} from "features/organizations/services/organizationAdminApi";
import { useOrgPermissions } from "features/organizations/lib/orgPermissions";
import {
  getAssignedLicenceMismatches,
  getDriverAssignedVehicleIds,
  getDriverDisplayName,
  getSelectedLicenceMismatches,
} from "features/organizations/lib/orgDriverVehicles";
import {
  getMemberDisplayName,
  getMembersWithoutDriverProfile,
} from "features/organizations/lib/orgMemberDisplay";
import { formatVehicleType } from "features/drivers/lib/driverLicenceStatus";
import { listMyVehicles } from "features/vehicles/services/vehicleApi";
import { queryKeys } from "core/query/queryKeys";
import { ROUTES } from "core/router/routes";
import OrgSwitcher from "shared/components/provider/OrgSwitcher";
import type {
  InvitationListItem,
  OrgDriver,
  OrgMember,
  OrgRole,
  VehicleListItem,
  VehicleType,
  VerificationStatus,
} from "types/api.generated";
import type { OrgDriverAssignedVehicle } from "features/organizations/lib/orgDriverVehicles";
import type { ApiError } from "types/api.types";

const VERIFICATION_BADGE: Record<string, string> = {
  unverified: "bg-slate-100 text-slate-700",
  pending: "bg-amber-50 text-amber-700",
  verified: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  expired: "bg-orange-50 text-orange-700",
};

export default function ProviderOrgAdmin() {
  const { user, hydrateFromMe } = useAuthStore();
  const { activeOrgId } = useProviderStore();
  const orgId = activeOrgId ?? "";
  const queryClient = useQueryClient();
  const currentUserId = user?.uid ?? "";

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState("");
  const [inviteVehicleIds, setInviteVehicleIds] = useState<string[]>([]);

  const rolesQuery = useQuery<OrgRole[]>({
    queryKey: queryKeys.organizations.roles(orgId),
    queryFn: listOrgRoles,
    enabled: Boolean(orgId),
  });

  const invitationsQuery = useQuery<InvitationListItem[]>({
    queryKey: queryKeys.organizations.invitations(orgId),
    queryFn: listOrgInvitations,
    enabled: Boolean(orgId),
  });

  const driversQuery = useQuery<OrgDriver[]>({
    queryKey: queryKeys.organizations.drivers(orgId),
    queryFn: listOrgDrivers,
    enabled: Boolean(orgId),
  });

  const membersQuery = useQuery<OrgMember[]>({
    queryKey: queryKeys.organizations.members(orgId),
    queryFn: listOrgMembers,
    enabled: Boolean(orgId),
  });

  const vehiclesQuery = useQuery<VehicleListItem[]>({
    queryKey: queryKeys.vehicles.mine,
    queryFn: listMyVehicles,
  });

  const inviteMutation = useMutation({
    mutationFn: () =>
      createOrgInvitation({
        email: inviteEmail.trim(),
        roleId: inviteRoleId,
        vehicleIds: inviteVehicleIds.length ? inviteVehicleIds : undefined,
      }),
    onSuccess: () => {
      setInviteEmail("");
      setInviteVehicleIds([]);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.invitations(orgId),
      });
    },
  });

  const driverRole = rolesQuery.data?.find((r) => r.slug === "driver");
  const fleetVehicles = vehiclesQuery.data ?? [];
  const drivers = useMemo(
    () => driversQuery.data ?? [],
    [driversQuery.data],
  );

  const ownerUserIds = useMemo(() => {
    const ids = new Set<string>();
    membersQuery.data?.forEach((m) => {
      if (m.role.slug === "owner") ids.add(m.userId);
    });
    return ids;
  }, [membersQuery.data]);

  const driverUserIds = useMemo(
    () => new Set(drivers.map((d) => d.userId)),
    [drivers],
  );

  const membersNotOnRoster = useMemo(
    () =>
      getMembersWithoutDriverProfile(membersQuery.data ?? [], driverUserIds),
    [membersQuery.data, driverUserIds],
  );

  const memberByUserId = useMemo(() => {
    const map = new Map<string, OrgMember>();
    membersQuery.data?.forEach((m) => map.set(m.userId, m));
    return map;
  }, [membersQuery.data]);

  const permissions = useOrgPermissions(orgId);

  const invalidateOrgData = async () => {
    await invalidateFleetQueries(queryClient, { orgId });
    await hydrateFromMe();
  };

  const orgRoles = rolesQuery.data ?? [];

  return (
    <div className="min-h-screen bg-background-light px-6 py-10 font-display">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black">Organisation</h1>
            <p className="text-sm text-slate-600 mt-1">
              Invite team members, manage drivers, and assign fleet access.
            </p>
          </div>
          <Link
            to={ROUTES.PROVIDER.DASHBOARD}
            className="text-sm font-bold text-primary shrink-0"
          >
            Dashboard
          </Link>
        </div>
        <OrgSwitcher />

        <section className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4">
          <h2 className="font-bold">Invite member</h2>
          <input
            type="email"
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          />
          <select
            value={inviteRoleId}
            onChange={(e) => setInviteRoleId(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
          >
            <option value="">Select role</option>
            {rolesQuery.data?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {driverRole && inviteRoleId === driverRole.id && (
            <div className="space-y-2 rounded-xl bg-slate-50 border border-slate-100 p-4">
              <p className="text-xs font-bold text-slate-500 uppercase">
                Fleet access (optional)
              </p>
              <p className="text-xs text-slate-600">
                Pre-select vehicles this driver can use when they accept the
                invite.
              </p>
              {fleetVehicles.length === 0 ? (
                <p className="text-xs text-slate-500">
                  No vehicles in this org yet. Add one from the dashboard first.
                </p>
              ) : (
                fleetVehicles.map((v) => (
                  <label
                    key={v.id}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={inviteVehicleIds.includes(v.id)}
                      onChange={(e) => {
                        setInviteVehicleIds((ids) =>
                          e.target.checked
                            ? [...ids, v.id]
                            : ids.filter((id) => id !== v.id),
                        );
                      }}
                      className="rounded border-slate-300"
                    />
                    {vehicleLabel(v)}
                  </label>
                ))
              )}
            </div>
          )}
          <button
            type="button"
            disabled={!inviteEmail || !inviteRoleId || inviteMutation.isPending}
            onClick={() => inviteMutation.mutate()}
            className="bg-primary text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-50"
          >
            Send invite
          </button>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-bold mb-1">Pending invitations</h2>
          <p className="text-sm text-slate-500 mb-4">
            Invites waiting to be accepted.
          </p>
          {invitationsQuery.isLoading && (
            <p className="text-sm text-slate-400">Loading…</p>
          )}
          {!invitationsQuery.isLoading &&
            invitationsQuery.data?.length === 0 && (
              <p className="text-sm text-slate-500">No pending invitations.</p>
            )}
          <ul className="space-y-3">
            {invitationsQuery.data?.map((inv) => (
              <li
                key={inv.id}
                className="flex flex-wrap justify-between gap-2 text-sm border-b border-slate-50 pb-3 last:border-0"
              >
                <span>
                  {inv.email} · {inv.role.name}
                </span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    className="text-primary font-semibold"
                    onClick={() =>
                      void resendOrgInvitation(inv.id).then(() =>
                        invitationsQuery.refetch(),
                      )
                    }
                  >
                    Resend
                  </button>
                  <button
                    type="button"
                    className="text-red-600 font-semibold"
                    onClick={() =>
                      void revokeOrgInvitation(inv.id).then(() =>
                        invitationsQuery.refetch(),
                      )
                    }
                  >
                    Revoke
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-bold mb-1">Members</h2>
          <p className="text-sm text-slate-600 mb-4">
            Everyone in the organisation who is not on the fleet driver roster.
            Enable a member as a driver to add them to the roster below.
          </p>

          {membersQuery.isLoading && (
            <p className="text-sm text-slate-400">Loading members…</p>
          )}

          {membersQuery.isError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              Could not load team members.
            </p>
          )}

          {!membersQuery.isLoading &&
            !membersQuery.isError &&
            membersNotOnRoster.length === 0 && (
              <p className="text-sm text-slate-500">
                All members are on the fleet driver roster, or invite someone
                above.
              </p>
            )}

          <ul className="space-y-3">
            {membersNotOnRoster.map((member) => (
              <TeamMemberRow
                key={member.id}
                member={member}
                roles={orgRoles}
                orgId={orgId}
                currentUserId={currentUserId}
                canUpdate={permissions.canUpdateMember}
                canDelete={permissions.canDeleteMember}
                canEnableDriver={permissions.canManageFleet}
                onChanged={() => void invalidateOrgData()}
              />
            ))}
          </ul>
        </section>

        {permissions.canReadDrivers && (
        <section className="bg-white rounded-2xl border border-slate-100 p-6">
          <h2 className="font-bold mb-1">Fleet drivers</h2>
          <p className="text-sm text-slate-600 mb-6">
            Drivers enabled for this organisation. Assign fleet vehicles and view
            licence progress. Platform admins approve licences.
          </p>

          {driversQuery.isLoading && (
            <p className="text-sm text-slate-400">Loading drivers…</p>
          )}

          {driversQuery.isError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              Could not load drivers for this organisation.
            </p>
          )}

          {!driversQuery.isLoading &&
            !driversQuery.isError &&
            drivers.length === 0 && (
              <div className="text-center py-10 px-4 rounded-xl bg-slate-50 border border-dashed border-slate-200">
                <span
                  className="material-symbols-outlined text-4xl text-slate-300 mb-2 block"
                  aria-hidden
                >
                  groups
                </span>
                <p className="font-semibold text-slate-700">No fleet drivers yet</p>
                <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                  Enable an existing member from the list above, or invite
                  someone with the driver role.
                </p>
              </div>
            )}

          <ul className="space-y-4">
            {drivers.map((d) => (
              <DriverCard
                key={d.userId}
                driver={d}
                vehicles={fleetVehicles}
                orgId={orgId}
                isOrgOwner={ownerUserIds.has(d.userId)}
                member={memberByUserId.get(d.userId)}
                currentUserId={currentUserId}
                canUpdateMember={permissions.canUpdateMember}
                canRemoveMember={permissions.canDeleteMember}
                canManageFleet={permissions.canManageFleet}
                roles={orgRoles}
                onChanged={() => void invalidateOrgData()}
                onSaved={() =>
                  queryClient.invalidateQueries({
                    queryKey: queryKeys.organizations.drivers(orgId),
                  })
                }
                onSave={(vehicleIds) =>
                  setDriverVehicleAccess(d.userId, { vehicleIds }, orgId)
                }
              />
            ))}
          </ul>
        </section>
        )}
      </div>
    </div>
  );
}

const ROLE_BADGE: Record<string, string> = {
  owner: "bg-slate-200 text-slate-800",
  admin: "bg-indigo-50 text-indigo-800",
  editor: "bg-violet-50 text-violet-800",
};

function TeamMemberRow({
  member,
  roles,
  orgId,
  currentUserId,
  canUpdate,
  canDelete,
  canEnableDriver,
  onChanged,
}: {
  member: OrgMember;
  roles: OrgRole[];
  orgId: string;
  currentUserId: string;
  canUpdate: boolean;
  canDelete: boolean;
  canEnableDriver: boolean;
  onChanged: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const isSelf = member.userId === currentUserId;
  const roleSlug = member.role.slug;
  const badgeClass = ROLE_BADGE[roleSlug] ?? "bg-slate-100 text-slate-700";

  const updateRoleMutation = useMutation({
    mutationFn: (roleId: string) =>
      updateOrgMemberRole(member.id, { roleId }, orgId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      setError((err as ApiError).message || "Could not update role.");
    },
  });

  const enableDriverMutation = useMutation({
    mutationFn: () => enableOrgDriver(member.userId, orgId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      setError((err as ApiError).message || "Could not enable driver.");
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => removeOrgMember(member.id, orgId),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      setError((err as ApiError).message || "Could not remove member.");
    },
  });

  const handleEnableDriver = () => {
    const name = getMemberDisplayName(member);
    if (
      !window.confirm(
        `Enable ${name} as a fleet driver? They can submit licences and go online once approved.`,
      )
    ) {
      return;
    }
    setError(null);
    enableDriverMutation.mutate();
  };

  const handleRemove = () => {
    const name = getMemberDisplayName(member);
    if (
      !window.confirm(
        `Remove ${name} from this organisation? This removes their access and clears any driver sessions and vehicle access.`,
      )
    ) {
      return;
    }
    setError(null);
    removeMutation.mutate();
  };

  const showRoleSelect = canUpdate && roles.length > 0;
  const showRemove = canDelete && !isSelf;
  const showEnableDriver = canEnableDriver && !isSelf;
  const pending =
    updateRoleMutation.isPending ||
    removeMutation.isPending ||
    enableDriverMutation.isPending;

  return (
    <li className="rounded-xl border border-slate-100 bg-slate-50/50 px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate">
            {getMemberDisplayName(member)}
            {isSelf && (
              <span className="text-xs font-normal text-slate-500 ml-1">
                (you)
              </span>
            )}
          </p>
          <p className="text-sm text-slate-500 truncate">{member.user.email}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {showEnableDriver && (
            <button
              type="button"
              disabled={pending}
              onClick={handleEnableDriver}
              className="text-xs font-bold text-primary bg-white border border-primary/20 px-3 py-1.5 rounded-lg hover:bg-primary/5 disabled:opacity-40"
            >
              {enableDriverMutation.isPending ? "Enabling…" : "Enable as driver"}
            </button>
          )}

          {showRoleSelect ? (
            <select
              value={member.roleId}
              disabled={pending}
              onChange={(e) => {
                const roleId = e.target.value;
                if (roleId === member.roleId) return;
                updateRoleMutation.mutate(roleId);
              }}
              className="text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 max-w-[9rem]"
              aria-label={`Role for ${getMemberDisplayName(member)}`}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          ) : (
            <span
              className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full ${badgeClass}`}
            >
              {member.role.name}
            </span>
          )}

          {showRemove && (
            <button
              type="button"
              disabled={pending}
              onClick={handleRemove}
              className="text-xs font-bold text-red-600 hover:text-red-700 disabled:opacity-40"
            >
              {removeMutation.isPending ? "Removing…" : "Remove member"}
            </button>
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
    </li>
  );
}

function vehicleLabel(
  v: Pick<VehicleListItem, "label" | "plateNumber" | "vehicleType">,
) {
  const title = v.label || v.plateNumber || "Vehicle";
  return `${title} · ${formatVehicleType(v.vehicleType)}`;
}

function vehicleShortName(v: {
  label: string | null;
  plateNumber: string | null;
  vehicleType: VehicleType;
}) {
  return v.label || v.plateNumber || formatVehicleType(v.vehicleType);
}

function formatMismatchWarning(
  selectedMismatches: VehicleListItem[],
  savedMismatches: OrgDriverAssignedVehicle[],
  dirty: boolean,
): string {
  const list = dirty ? selectedMismatches : savedMismatches;
  if (list.length === 0) return "";
  const names = list.map((v) => vehicleShortName(v)).join(", ");
  const plural = list.length > 1;
  return `${names} ${plural ? "are" : "is"} assigned without a matching verified licence. The driver cannot go online with ${plural ? "those vehicles" : "that vehicle"} until the licence type is approved.`;
}

function DriverCard({
  driver,
  vehicles,
  orgId,
  isOrgOwner,
  member,
  currentUserId,
  canUpdateMember,
  canRemoveMember,
  canManageFleet,
  roles,
  onChanged,
  onSave,
  onSaved,
}: {
  driver: OrgDriver;
  vehicles: VehicleListItem[];
  orgId: string;
  isOrgOwner: boolean;
  member?: OrgMember;
  currentUserId: string;
  canUpdateMember: boolean;
  canRemoveMember: boolean;
  canManageFleet: boolean;
  roles: OrgRole[];
  onChanged: () => void;
  onSave: (vehicleIds: string[]) => Promise<unknown>;
  onSaved: () => void;
}) {
  const [memberError, setMemberError] = useState<string | null>(null);
  const isSelf = driver.userId === currentUserId;
  const memberId = member?.id;
  const showRemoveFromFleet = canManageFleet && !isSelf;
  const showRemoveMember = canRemoveMember && Boolean(memberId) && !isSelf;
  const showRoleSelect = canUpdateMember && Boolean(memberId) && roles.length > 0;

  const updateRoleMutation = useMutation({
    mutationFn: (roleId: string) => {
      if (!memberId) throw new Error("Member not found");
      return updateOrgMemberRole(memberId, { roleId }, orgId);
    },
    onSuccess: () => {
      setMemberError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      setMemberError((err as ApiError).message || "Could not update role.");
    },
  });

  const disableFleetMutation = useMutation({
    mutationFn: () => disableOrgDriver(driver.userId, orgId),
    onSuccess: () => {
      setMemberError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      setMemberError(
        (err as ApiError).message || "Could not remove from fleet.",
      );
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: () => {
      if (!memberId) throw new Error("Member not found");
      return removeOrgMember(memberId, orgId);
    },
    onSuccess: () => {
      setMemberError(null);
      onChanged();
    },
    onError: (err: unknown) => {
      setMemberError(
        (err as ApiError).message || "Could not remove member.",
      );
    },
  });

  const handleRemoveFromFleet = () => {
    const name = getDriverDisplayName(driver);
    if (
      !window.confirm(
        `Remove ${name} from the fleet driver roster? They remain a team member but cannot go online or use fleet vehicles until re-enabled.`,
      )
    ) {
      return;
    }
    setMemberError(null);
    disableFleetMutation.mutate();
  };

  const handleRemoveMember = () => {
    const name = getDriverDisplayName(driver);
    if (
      !window.confirm(
        `Remove ${name} from this organisation? This removes their access and clears any driver sessions and vehicle access.`,
      )
    ) {
      return;
    }
    setMemberError(null);
    removeMemberMutation.mutate();
  };

  const actionPending =
    updateRoleMutation.isPending ||
    disableFleetMutation.isPending ||
    removeMemberMutation.isPending;

  const status = driver.verificationStatus as VerificationStatus;
  const statusLabel = status.replace("_", " ");
  return (
    <li
      className={`rounded-2xl border overflow-hidden ${
        isOrgOwner ? "border-slate-200" : "border-slate-100"
      }`}
    >
      <div
        className={`px-5 py-4 border-b border-slate-100 flex flex-wrap items-start justify-between gap-3 ${
          isOrgOwner ? "bg-slate-100/70" : "bg-slate-50/80"
        }`}
      >
        <div>
          <p className="font-bold text-slate-900 text-lg">
            {getDriverDisplayName(driver)}
          </p>
          <p className="text-sm text-slate-500">{driver.user.email}</p>
          {isOrgOwner && (
            <p className="text-xs text-slate-500 mt-1">Organisation owner</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {isOrgOwner && (
            <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-slate-200 text-slate-700">
              Owner
            </span>
          )}
          {showRoleSelect && member && (
            <select
              value={member.roleId}
              disabled={actionPending}
              onChange={(e) => {
                const roleId = e.target.value;
                if (roleId === member.roleId) return;
                updateRoleMutation.mutate(roleId);
              }}
              className="text-sm rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 max-w-[9rem]"
              aria-label={`Role for ${getDriverDisplayName(driver)}`}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
          <span
            className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full ${
              VERIFICATION_BADGE[status] ?? VERIFICATION_BADGE.unverified
            }`}
          >
            {statusLabel}
          </span>
          <Link
            to={ROUTES.PROVIDER.ORG_DRIVER_LICENCES(driver.userId)}
            className="text-xs font-bold text-primary bg-white border border-primary/20 px-3 py-1.5 rounded-lg hover:bg-primary/5"
          >
            View licence progress
          </Link>
          {showRemoveFromFleet && (
            <button
              type="button"
              disabled={actionPending}
              onClick={handleRemoveFromFleet}
              className="text-xs font-bold text-amber-800 bg-white border border-amber-200 px-3 py-1.5 rounded-lg hover:bg-amber-50 disabled:opacity-40"
            >
              {disableFleetMutation.isPending
                ? "Removing…"
                : "Remove from fleet"}
            </button>
          )}
          {showRemoveMember && (
            <button
              type="button"
              disabled={actionPending}
              onClick={handleRemoveMember}
              className="text-xs font-bold text-red-600 bg-white border border-red-200 px-3 py-1.5 rounded-lg hover:bg-red-50 disabled:opacity-40"
            >
              {removeMemberMutation.isPending
                ? "Removing…"
                : "Remove member"}
            </button>
          )}
        </div>
      </div>

      {memberError && (
        <p className="text-sm text-red-600 bg-red-50 border-b border-red-100 px-5 py-3">
          {memberError}
        </p>
      )}

      <div className="p-5 space-y-5">
        <LicenceTypesSection driver={driver} />

        {isOrgOwner ? (
          <OwnerFleetSection driver={driver} vehicles={vehicles} />
        ) : (
          <EditableFleetSection
            driver={driver}
            vehicles={vehicles}
            orgId={orgId}
            canManageFleet={canManageFleet}
            onSave={onSave}
            onSaved={onSaved}
          />
        )}
      </div>
    </li>
  );
}

function LicenceTypesSection({ driver }: { driver: OrgDriver }) {
  return (
    <div>
      <p className="text-xs font-bold text-slate-500 uppercase mb-2">
        Verified licences
      </p>
      {driver.licenceVehicleTypes.length === 0 ? (
        <p className="text-sm text-slate-500">
          No active verified licence yet.{" "}
          <Link
            to={ROUTES.PROVIDER.ORG_DRIVER_LICENCES(driver.userId)}
            className="font-semibold text-primary"
          >
            View licence progress
          </Link>
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {driver.licenceVehicleTypes.map((type) => (
            <span
              key={type}
              className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-800"
            >
              {formatVehicleType(type)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function OwnerFleetSection({
  driver,
  vehicles,
}: {
  driver: OrgDriver;
  vehicles: VehicleListItem[];
}) {
  const fleetMismatches = useMemo(
    () =>
      getSelectedLicenceMismatches(
        driver,
        vehicles.map((v) => v.id),
        vehicles,
      ),
    [driver, vehicles],
  );

  return (
    <div>
      <p className="text-xs font-bold text-slate-500 uppercase mb-2">
        Fleet vehicles
      </p>
      <p className="text-sm text-slate-600 mb-3">
        As the organisation owner, they have access to every vehicle in the
        fleet when going online.
      </p>

      {fleetMismatches.length > 0 && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-3">
          {formatMismatchWarning(fleetMismatches, [], true)}
        </p>
      )}

      {vehicles.length === 0 ? (
        <EmptyFleetMessage />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {vehicles.map((v) => {
            const lacksLicence = !driver.licenceVehicleTypes.includes(
              v.vehicleType,
            );
            return (
              <VehicleTile
                key={v.id}
                vehicle={v}
                accessLabel={
                  lacksLicence ? "No matching licence" : "Full access"
                }
                className={
                  lacksLicence
                    ? "border-amber-400 bg-amber-50/80 ring-1 ring-amber-200"
                    : "border-slate-200 bg-slate-50/80"
                }
                labelClassName={
                  lacksLicence ? "text-amber-700" : "text-slate-600"
                }
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditableFleetSection({
  driver,
  vehicles,
  orgId,
  canManageFleet,
  onSave,
  onSaved,
}: {
  driver: OrgDriver;
  vehicles: VehicleListItem[];
  orgId: string;
  canManageFleet: boolean;
  onSave: (vehicleIds: string[]) => Promise<unknown>;
  onSaved: () => void;
}) {
  const assignedFromApi = useMemo(
    () => getDriverAssignedVehicleIds(driver),
    [driver],
  );

  const [selected, setSelected] = useState<string[]>(assignedFromApi);
  const [baseline, setBaseline] = useState<string[]>(assignedFromApi);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSelected(assignedFromApi);
    setBaseline(assignedFromApi);
    setSaved(false);
    setSaveError(null);
  }, [driver.userId, assignedFromApi]);

  const dirty =
    JSON.stringify([...selected].sort()) !==
    JSON.stringify([...baseline].sort());

  const selectedMismatches = useMemo(
    () => getSelectedLicenceMismatches(driver, selected, vehicles),
    [driver, selected, vehicles],
  );

  const savedMismatches = useMemo(
    () => getAssignedLicenceMismatches(driver),
    [driver],
  );

  const toggleVehicle = (vehicleId: string) => {
    setSaved(false);
    setSelected((ids) =>
      ids.includes(vehicleId)
        ? ids.filter((id) => id !== vehicleId)
        : [...ids, vehicleId],
    );
  };

  return (
    <div>
      <p className="text-xs font-bold text-slate-500 uppercase mb-2">
        Assigned fleet vehicles
      </p>
      <p className="text-sm text-slate-600 mb-3">
        Select which org vehicles this driver may use when going online, then
        save.
      </p>

      {(selectedMismatches.length > 0 ||
        (!dirty && savedMismatches.length > 0)) && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-3">
          {formatMismatchWarning(selectedMismatches, savedMismatches, dirty)}
        </p>
      )}

      {vehicles.length === 0 ? (
        <EmptyFleetMessage />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {vehicles.map((v) => {
            const isSelected = selected.includes(v.id);
            const lacksLicence =
              isSelected && !driver.licenceVehicleTypes.includes(v.vehicleType);
            return (
            <button
              key={v.id}
                type="button"
                disabled={!canManageFleet}
                onClick={() => toggleVehicle(v.id)}
                className={`text-left rounded-xl border px-4 py-3 transition-colors ${
                  lacksLicence
                    ? "border-amber-400 bg-amber-50/80 ring-1 ring-amber-200"
                    : isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                      : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <VehicleTileContent vehicle={v} />
                <p
                  className={`text-[10px] font-bold uppercase mt-1.5 ${
                    lacksLicence
                      ? "text-amber-700"
                      : isSelected
                        ? "text-primary"
                        : "text-slate-400"
                  }`}
                >
                  {lacksLicence
                    ? "No matching licence"
                    : isSelected
                      ? "Assigned"
                      : "Not assigned"}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {saveError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3 mt-3">
          {saveError}
        </p>
      )}

      {vehicles.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 pt-4">
          <button
            type="button"
            disabled={saving || !dirty || !orgId || !canManageFleet}
            onClick={() => {
              setSaving(true);
              setSaved(false);
              setSaveError(null);
              void onSave(selected)
                .then(() => {
                  onSaved();
                  setBaseline([...selected]);
                  setSaved(true);
                })
                .catch((err: unknown) => {
                  const apiErr = err as ApiError;
                  setSaveError(
                    apiErr.message ||
                      "Could not save vehicle access. Try again.",
                  );
                })
                .finally(() => setSaving(false));
            }}
            className="bg-primary text-white font-bold px-4 py-2 rounded-xl text-sm disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          {saved && !dirty && (
            <span className="text-sm text-emerald-700 font-medium">Saved</span>
          )}
        </div>
      )}
    </div>
  );
}

function VehicleTileContent({
  vehicle: v,
}: {
  vehicle: Pick<VehicleListItem, "label" | "plateNumber" | "vehicleType">;
}) {
  return (
    <>
      <p className="text-sm font-semibold text-slate-800">
        {v.label || v.plateNumber || "Vehicle"}
      </p>
      <p className="text-xs text-slate-500">
        {formatVehicleType(v.vehicleType)}
        {v.plateNumber && v.label ? ` · ${v.plateNumber}` : ""}
      </p>
    </>
  );
}

function VehicleTile({
  vehicle,
  accessLabel,
  className,
  labelClassName,
}: {
  vehicle: VehicleListItem;
  accessLabel: string;
  className: string;
  labelClassName: string;
}) {
  return (
    <div className={`rounded-xl border px-4 py-3 ${className}`}>
      <VehicleTileContent vehicle={vehicle} />
      <p className={`text-[10px] font-bold uppercase mt-1.5 ${labelClassName}`}>
        {accessLabel}
      </p>
    </div>
  );
}

function EmptyFleetMessage() {
  return (
    <p className="text-sm text-slate-500 bg-slate-50 rounded-xl px-4 py-3">
      No vehicles in this organisation.{" "}
      <Link
        to={ROUTES.PROVIDER.DASHBOARD}
        className="font-semibold text-primary"
      >
        Add a vehicle
      </Link>{" "}
      on the dashboard first.
    </p>
  );
}
